import {
  Component,
  OnInit,
  AfterViewInit,
  ViewEncapsulation,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ProcessesBottomSheetComponent } from '../dialogs/processes-bottom-sheet/processes-bottom-sheet.component';
import { CrudEmployeeService } from '../../content/crud/crud-employee/crud-employee.service';
import { CrudOrganizationService } from '../../content/crud/crud-organization/crud-organization.service';
import { Router } from '@angular/router';
import { GT2FirebaseService } from '../../../services/gt2-firebase.service';
import { EmployeeModelData } from '../../api/models/CrudEmployeeModel';
import { AuthenticationService } from '../../api/auth-service.service';
import { combineLatest, interval, Subscription, timer } from 'rxjs';
import { filter, take, timeout, catchError } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FuseDirectivesModule } from '../../../../@fuse/directives/directives';
import { FusePipesModule } from '../../../../@fuse/pipes/pipes.module';
import { Gt2PrimengModule } from '../../modules/gt2-primeng.module';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-firebase-process',
  templateUrl: './firebase-process.component.html',
  styleUrls: ['./firebase-process.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FuseDirectivesModule,
    FusePipesModule,
    Gt2PrimengModule,
    MatButtonModule,
    TranslateModule,
    MatInputModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FirebaseProcessComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _bottomSheetRef: any;
  private firebaseSubscription!: Subscription | null;
  // Last list received from Firebase; re-evaluated every minute so an errored
  // row whose `expired` has passed disappears even without a new Firebase event.
  private lastProcesses: any[] = [];
  private expiryTickSubscription: Subscription | null = null;
  private hasFocus: boolean = true;
  private automationMode: boolean = false;

  constructor(
    private logger: NGXLogger,
    private bottomSheet: MatBottomSheet,
    private employeeService: CrudEmployeeService,
    private orgService: CrudOrganizationService,
    private router: Router,
    private auth: AuthenticationService,
    private gt2FirebaseService: GT2FirebaseService,
  ) {
    this.automationMode = window.location.search.includes('automation=true');
    // //this.logger.info("FirebaseProcessComponent.constructor()");

    // Listen for Firebase auth ready (works for both fresh login and page reload via ReplaySubject)
    this.auth.firebaseAuthReady.subscribe({
      next: () => {
        console.log('[Firebase] FirebaseProcessComponent -- > firebaseAuthReady, calling loadData()');
        this.loadData();
      },
    });

    this.auth.logoutSubject.subscribe({
      next: (value) => {
        // //this.logger.info("FirebaseProcessComponent.logoutSubject()");
        if (this._bottomSheetRef) {
          this._bottomSheetRef.dismiss(null);
        }

        // //this.logger.info("FirebaseProcessComponent.logoutSubject() -> logoutSubject this.firebaseSubscription: " + this.firebaseSubscription);
        if (this.firebaseSubscription) {
          this.firebaseSubscription.unsubscribe();
          this.firebaseSubscription = null;
          // //this.logger.info("FirebaseProcessComponent.logoutSubject() -> logoutSubject this.firebaseSubscription.unsubscribe()");
        }

        this.gt2FirebaseService.unsubscribeProcesses();
      },
    });
  }

  ngOnInit() {
    // //this.logger.info("FirebaseProcessComponent.ngOnInit()");
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    if (this.expiryTickSubscription) {
      this.expiryTickSubscription.unsubscribe();
      this.expiryTickSubscription = null;
    }
    if (this.firebaseSubscription) {
      this.firebaseSubscription.unsubscribe();
    }

    this.gt2FirebaseService.unsubscribeProcesses();
  }

  @HostListener('window:focus', ['$event'])
  onFocus(event: FocusEvent): void {
    // //this.logger.info("FirebaseProcessComponent.@HostListener().onFocus() -> BROWSER TAB: THIS IS onFocus()");
    this.hasFocus = true;
  }

  @HostListener('window:blur', ['$event'])
  onBlur(event: FocusEvent): void {
    // //this.logger.info("FirebaseProcessComponent.@HostListener().onBlur() -> BROWSER TAB: THIS IS onBlur()");
    this.hasFocus = false;
  }

  //
  public loadData(): void {
    console.log('[Firebase] loadData() called');
    const organizationMe = this.orgService.getOrganizationMeObject();

    // Use loadMe() instead of getMe() - getMe() returns a Subject that may have already emitted
    // Also check if employee is already cached
    const cachedEmployee = this.employeeService.getMeMe();

    if (organizationMe !== null && cachedEmployee) {
      console.log('[Firebase] loadData() - using cached data, org:', organizationMe.data?.slug, 'employee:', cachedEmployee?.user?.uuid);
      this.setUpFirebase(cachedEmployee, organizationMe.data);
    } else {
      console.log('[Firebase] loadData() - fetching from API, orgCached:', !!organizationMe, 'employeeCached:', !!cachedEmployee);
      const combined = combineLatest([
        organizationMe ? [organizationMe] : this.orgService.loadOrganizationMe('fb.loadData'),
        cachedEmployee ? [cachedEmployee] : this.employeeService.loadMe(),
      ]);
      combined.subscribe({
        next: (latestValues) => {
          const org: any = organizationMe ? organizationMe.data : (latestValues[0] as any).data;
          const employee: any = cachedEmployee ? cachedEmployee : latestValues[1];
          console.log('[Firebase] loadData() - API returned org:', org?.slug, 'employee:', employee?.user?.uuid);
          this.setUpFirebase(employee, org);
        },
        error: (error) => {
          console.error('[Firebase] loadData() - API call FAILED:', error);
        },
      });
    }
  }

  public setUpFirebase(employee: EmployeeModelData, org: any): void {
    if (this.firebaseSubscription) {
      this.firebaseSubscription.unsubscribe();
      this.firebaseSubscription = null;
    }

    this.firebaseSubscription = this.gt2FirebaseService.onProcesses.subscribe({
      next: (processes: any[]) => {
        this.lastProcesses = processes || [];
        const inProgress: any[] = this.applyDrawerVisibility(this.lastProcesses);

        if (this.hasFocus || this.automationMode) {
          for (let i: number = 0; i < inProgress.length; i++) {
            this.gt2FirebaseService.acceptProcess(inProgress[i]);
          }
        }

        const completed: any[] = this.lastProcesses.filter((item) => {
          return item.status === GT2FirebaseService.PROCESS_COMPLETED;
        });

        //
        if (completed && completed.length > 0) {
          this.gt2FirebaseService.processesCompletedActionDispatch(completed);
        }
      },
    });
    //
    if (!this.expiryTickSubscription) {
      this.expiryTickSubscription = interval(60 * 1000).subscribe(() => {
        this.applyDrawerVisibility(this.lastProcesses);
      });
    }
    //
    this.gt2FirebaseService.setupFirebaseProcesses(employee, org);
  }

  /**
   * Decides which rows the bottom drawer shows and opens/updates/dismisses it.
   * `in-progress` rows are always shown (the server-side sweep is the authority
   * on stale ones). `error` rows are shown until their `expired` (backend sets
   * it to +5 min when it flips a process to error) and then deleted from
   * Firebase, so a failed job no longer pins a red drawer until the nightly wipe.
   */
  private applyDrawerVisibility(processes: any[]): any[] {
    const inProgress: any[] = [];
    for (const item of processes) {
      if (item.status === GT2FirebaseService.PROCESS_IN_PROGRESS) {
        inProgress.push(item);
      } else if (item.status === GT2FirebaseService.PROCESS_ERROR) {
        if (this.isExpired(item)) {
          this.gt2FirebaseService.deleteProcess(item);
        } else {
          inProgress.push(item);
        }
      }
    }

    if (inProgress.length > 0 && !this._bottomSheetRef && (this.hasFocus || this.automationMode)) {
      this._bottomSheetRef = this.bottomSheet.open(
        ProcessesBottomSheetComponent,
        {
          disableClose: false,
          hasBackdrop: false,
          panelClass: 'custom-sheet-container',
          data: {
            processes: inProgress,
          },
        },
      );

      this._bottomSheetRef.afterDismissed().subscribe({
        next: (result: any) => {
          if (result) {
          }
          this._bottomSheetRef = null;
        },
      });
    } else if (inProgress.length > 0 && this._bottomSheetRef) {
      this._bottomSheetRef.instance.setProcesses(inProgress);
    } else if (inProgress.length === 0 && this._bottomSheetRef) {
      this._bottomSheetRef.dismiss(null);
    }

    return inProgress;
  }

  // `expired` is "YYYY-MM-DD HH:mm" in the tenant timezone (browser-local for
  // our users). Unparsable/missing → not expired (never hide on a guess).
  private isExpired(item: any): boolean {
    if (!item || typeof item.expired !== 'string' || item.expired === '') {
      return false;
    }
    const at = new Date(item.expired.replace(' ', 'T'));
    if (isNaN(at.getTime())) {
      return false;
    }
    return at.getTime() < Date.now();
  }
}
